import {
  IExperienceTemplateResource,
  ISubTaskCompleteLmsModuleTemplate
} from '@dadohr/shared/domainTypes/experience';
import { DoceboCoursePublic } from '@dadohr/shared/domainTypes/integrations/docebo';
import { CustomUserField } from '@dadohr/shared/domainTypes/org';
import { TextField, Typography } from '@material-ui/core';
import { SetStateAction } from 'react';
import { AutocompleteSingle } from '../../../../../../../../components/Autocomplete';
import { CustomInputLabel } from '../../../../../../../../components/CustomInputLabel';
import {
  MarkdownEditorWithReplacementTokens,
  MarkdownTextWithReplacementPlaceholders,
  TokenDefinition
} from '../../../../../../../../components/MarkdownText';
import { useUnsafeTranslation } from '../../../../../../../../i18next';
import { FlexContainerVertical } from '../../../../../../../../layout/Flex';
import { toSearchTermRegexp } from '../../../../../../../../services/user';
import { useDoceboCourses } from '../../../../../../../Org/pages/Integrations/integrations/docebo/service';
import { BlockSettingsSubPage, TaskBuilderContext } from '../../../types';
import {
  TaskBuilderBlockValidationState,
  TaskBuilderBlockValidationStateItem,
  useFieldValidator,
  VALIDATORS_COMPLETE_LMS_MODULE
} from '../../../validation';

// Phase 1 supports COURSE units only; module / learning-path selection follows
// once their Docebo endpoints are wired (spec new-main-dj78.4).
export const BlockSettingsCompleteLmsModule = ({
  value,
  onChange,
  validation,
  setValidation,
  expTpl,
  tokens
}: {
  value: ISubTaskCompleteLmsModuleTemplate;
  onChange: (nextValue: ISubTaskCompleteLmsModuleTemplate) => void;
  validation: TaskBuilderBlockValidationStateItem | null;
  setValidation: React.Dispatch<SetStateAction<TaskBuilderBlockValidationState>>;
  expTpl: TaskBuilderContext;
  customFields: CustomUserField[];
  resources: IExperienceTemplateResource[];
  goToSubPage: (nextSubPage: BlockSettingsSubPage) => void;
  tokens: TokenDefinition[];
}) => {
  const { t } = useUnsafeTranslation();
  const [courses, coursesLoading, coursesError] = useDoceboCourses(expTpl.orgId);

  const validateSupportContact = useFieldValidator(
    setValidation,
    VALIDATORS_COMPLETE_LMS_MODULE,
    'supportContact'
  );
  const invalidSupportContactReason = validation?.fields.supportContact || null;

  const patchData = (data: Partial<ISubTaskCompleteLmsModuleTemplate['data']>) =>
    onChange({ ...value, data: { ...value.data, ...data } });

  const courseOptions: DoceboCoursePublic[] = courses ?? [];
  // Prefer the loaded option; fall back to a synthetic option carrying the
  // stored id+name so the selection still renders before/without a fresh fetch.
  const selectedCourse: DoceboCoursePublic | null =
    courseOptions.find((c) => String(c.id) === value.data.unit.id) ??
    (value.data.unit.id
      ? ({ id: value.data.unit.id, title: value.data.unit.name } as unknown as DoceboCoursePublic)
      : null);

  const courseHelperText = coursesLoading
    ? t('Loading courses…')
    : coursesError
    ? t('Could not load courses — check the Docebo connection.')
    : courseOptions.length === 0
    ? t('No courses found in Docebo.')
    : undefined;

  return (
    <FlexContainerVertical fullWidth spacing={3}>
      <CustomInputLabel fullWidth label={t('Course')}>
        <AutocompleteSingle
          fullWidth
          value={selectedCourse}
          options={courseOptions}
          placeholder={t('Select a course')}
          getOptionLabel={(o) => o?.title || ''}
          getOptionSelected={(o, v) => String(o?.id) === String(v?.id)}
          renderOption={(o) => o?.title || ''}
          filterOptions={(os, s) => {
            const re = toSearchTermRegexp(s.inputValue);
            return os.filter((o) => o && re.exec(o.title));
          }}
          onChange={(course) =>
            patchData({
              unit: {
                type: 'COURSE',
                id: course ? String(course.id) : '',
                name: course?.title || ''
              }
            })
          }
          helperText={courseHelperText}
        />
      </CustomInputLabel>

      <CustomInputLabel fullWidth label={t('Instructions')}>
        <MarkdownEditorWithReplacementTokens
          fullWidth
          value={value.data.description.t}
          onChange={(v) =>
            patchData({ description: { ...value.data.description, t: v } })
          }
          tokens={tokens}
        />
      </CustomInputLabel>

      <CustomInputLabel fullWidth label={t('Support contact email')}>
        <TextField
          variant="outlined"
          fullWidth
          required
          type="email"
          placeholder={t('help@yourcompany.com')}
          value={value.data.supportContact.email}
          error={!!invalidSupportContactReason}
          helperText={
            invalidSupportContactReason ||
            t('Shown to the participant as "Something not working? Contact …"')
          }
          onBlur={() => validateSupportContact(value, expTpl)}
          onChange={(ev) => {
            const nextValue = {
              ...value,
              data: {
                ...value.data,
                supportContact: {
                  ...value.data.supportContact,
                  email: ev.target.value
                }
              }
            };
            invalidSupportContactReason &&
              validateSupportContact(nextValue, expTpl);
            onChange(nextValue);
          }}
        />
      </CustomInputLabel>
    </FlexContainerVertical>
  );
};

export const BlockSettingsCompleteLmsModuleReadOnly = ({
  value,
  tokens
}: {
  value: ISubTaskCompleteLmsModuleTemplate;
  expTpl: TaskBuilderContext;
  tokens: TokenDefinition[];
}) => {
  const { t } = useUnsafeTranslation();
  return (
    <FlexContainerVertical fullWidth spacing={2}>
      <div>
        <Typography variant="subtitle2">{t('Course')}</Typography>
        <div>{value.data.unit.name || '-'}</div>
      </div>
      <div>
        <Typography variant="subtitle2">{t('Instructions')}</Typography>
        { value.data.description.t ? (
            <MarkdownTextWithReplacementPlaceholders
              t={value.data.description}
              throttleMs={500}
              tokens={tokens}
            />
          ): '-'
        }
      </div>
      <div>
        <Typography variant="subtitle2">{t('Support contact')}</Typography>
        <div>{value.data.supportContact.email || '-'}</div>
      </div>
    </FlexContainerVertical>
  );
};
